import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography, Box, Alert, } from "@mui/material";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PaymentIcon from "@mui/icons-material/Payment";
import "./PaymentDeleteDialog.css";

export default function PaymentDeleteDialog({
  open,
  onClose,
  payment,
  onDeleted,
}) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const rawPayment = useMemo(() => payment ?? null, [payment]);

  /* De fiecare dată când se deschide dialogul:
    - resetăm loading-ul
    - curățăm eventualele erori vechi
   */
  useEffect(() => {
    if (!open) return;
    setDeleting(false);
    setError("");
  }, [open]);

  //Formatează suma în stil românesc: punct la mii și virgulă la zecimale.
  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const n = Number(value);
    if (Number.isNaN(n)) return "";

    const parts = n.toFixed(2).split(".");
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const decimalPart = parts[1];

    return `${integerPart},${decimalPart}`;
  };

  // Formatează data într-un format mai ușor de citit: dd.mm.yyyy.
  const formatDateRO = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("ro-RO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  /* Declanșează ștergerea plății.
    Dacă merge bine, apelăm onDeleted cu id-ul plății.
    Dacă apare o problemă, afișăm mesaj de eroare.
   */
  const handleDelete = async () => {
    if (!rawPayment?.id) return;

    setError("");
    setDeleting(true);

    try {
      onDeleted?.(rawPayment.id);
    } catch (e) {
      setError(
        "Cannot delete this payment because it has dependencies in other records. Please remove or update those dependencies first."
      );
    } finally {
      setDeleting(false);
    }
  };

  // Luăm moneda din obiectul payment, dacă există.
  const currencyDisplay = rawPayment?.currency_label || rawPayment?.currency || "";

  // Pregătim suma pentru afișare împreună cu moneda.
  const amountDisplay = rawPayment?.amount
    ? `${formatNumber(rawPayment.amount)} ${currencyDisplay}`.trim()
    : "-";

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      className="payment-delete-dialog"
      PaperProps={{
        elevation: 24,
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header-ul apare doar cât timp nu avem eroare */}
      {!error && (
        <DialogTitle className="pd-header">
          <Box className="pd-header-content">
            <Box className="pd-title-section">
              <WarningAmberIcon className="pd-warning-icon" />
              <Typography variant="h5" component="span" className="pd-title">
                Delete Payment
              </Typography>
            </Box>

            {/* Butonul de închidere */}
            <IconButton
              onClick={onClose}
              className="pd-close-btn"
              size="small"
              disabled={deleting}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
      )}

      <DialogContent className="pd-content">
        {error ? (
          // Dacă ștergerea eșuează, afișăm un mesaj clar de eroare.
          <Alert
            severity="error"
            className="pd-error-alert"
            icon={<ErrorOutlineIcon />}
          >
            <Typography variant="body1" fontWeight={500}>
              Unable to Delete Payment
            </Typography>
            <Typography variant="body2" className="pd-error-message">
              {error}
            </Typography>
          </Alert>
        ) : (
          // Dacă nu există eroare, afișăm detaliile plății și mesajul de confirmare înainte de ștergere.
          <Box className="pd-confirm-content">
            <Box className="pd-payment-details">
              <Box className="pd-detail-item">
                <PaymentIcon className="pd-payment-icon" />
                <Typography variant="subtitle2" className="pd-detail-label">
                  Payment Amount
                </Typography>
                <Typography variant="h6" className="pd-amount-value">
                  {amountDisplay}
                </Typography>
              </Box>

              {rawPayment?.date && (
                <Box className="pd-detail-item">
                  <Typography variant="subtitle2" className="pd-detail-label">
                    Payment Date
                  </Typography>
                  <Typography variant="body1" className="pd-date-value">
                    {formatDateRO(rawPayment.date)}
                  </Typography>
                </Box>
              )}

              {rawPayment?.description && (
                <Box className="pd-detail-item">
                  <Typography variant="subtitle2" className="pd-detail-label">
                    Description
                  </Typography>
                  <Typography variant="body2" className="pd-description-value">
                    {rawPayment.description}
                  </Typography>
                </Box>
              )}
            </Box>

            <Typography variant="body1" className="pd-warning-text">
              This action is permanent and cannot be undone.
            </Typography>

            <Box className="pd-consequences">
              <Typography variant="body2" className="pd-consequence-item">
                • The payment will be permanently deleted from the system
              </Typography>
              <Typography variant="body2" className="pd-consequence-item">
                • The invoice total and remaining balance will be recalculated
              </Typography>
              <Typography variant="body2" className="pd-consequence-item">
                • This change cannot be reversed
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions className="pd-actions">
        {error ? (
          // Dacă a apărut eroare, lăsăm doar un buton simplu de închidere.
          <Button
            onClick={onClose}
            variant="contained"
            className="pd-btn pd-btn-ok"
          >
            Got It
          </Button>
        ) : (
          <>
            {/* Închide dialogul fără să șteargă */}
            <Button
              onClick={onClose}
              disabled={deleting}
              variant="outlined"
              className="pd-btn pd-btn-cancel"
            >
              Cancel
            </Button>

            {/* Confirmă ștergerea */}
            <Button
              onClick={handleDelete}
              disabled={deleting || !rawPayment?.id}
              variant="contained"
              className="pd-btn pd-btn-delete"
              startIcon={
                deleting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <DeleteOutlineIcon />
                )
              }
              autoFocus
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}